import React from 'react';

import { Modal, Stack, Text } from '@mantine/core';
import { AxiosError, AxiosResponse } from 'axios';
import { UseMutationResult } from 'react-query';
import { AthletePackage, AthletePackageCancel, AthletePackageRetrieve, ErrorMessage } from 'types';

import { CancelModalProvider, useCancelModal } from './CancelModalContext';
import CancellationSettingsStep from './components/CancellationSettingsStep';
import ReservationHandlingStep from './components/ReservationHandlingStep';
import ReviewStep from './components/ReviewStep';
import Wizard from './components/Wizard';
import PackageErrorMiscFeeModal from '../PackageErrorMiscFeeModal';

interface CancelModalProps {
  opened: boolean;
  athletePackage: AthletePackageRetrieve;
  closeHandler: () => void;
  isDesktop: boolean;
  cancelAthletePackageMutation: UseMutationResult<
    AxiosResponse<AthletePackage>,
    AxiosError<ErrorMessage>,
    AthletePackageCancel
  >;
}

const CancelModalShell: React.FC<{ opened: boolean }> = ({ opened }) => {
  const {
    isDesktop,
    handleClose,
    showErrorModal,
    errorModalData,
    handleGoToInvoice,
    currentStep,
    hasUpcomingReservations,
  } = useCancelModal();

  return (
    <>
      <Modal
        opened={opened && !showErrorModal}
        onClose={handleClose}
        size='lg'
        centered
        title={<Text size='lg'>{currentStep}</Text>}
      >
        <Stack p='md' h={isDesktop ? undefined : '90vh'}>
          <Wizard>
            <Wizard.Step name='Cancellation Settings'>
              <CancellationSettingsStep />
            </Wizard.Step>
            <Wizard.Step name='Reservation Handling' skip={!hasUpcomingReservations}>
              <ReservationHandlingStep />
            </Wizard.Step>
            <Wizard.Step name='Review'>
              <ReviewStep />
            </Wizard.Step>
          </Wizard>
        </Stack>
      </Modal>

      <PackageErrorMiscFeeModal
        opened={showErrorModal}
        onClose={handleClose}
        blockingInvoice={errorModalData?.blockingInvoiceId || null}
        onGoToInvoice={handleGoToInvoice}
        isChildPackageError={errorModalData?.isChildPackageError || false}
        operation='cancel'
      />
    </>
  );
};

const CancelModal: React.FC<CancelModalProps> = ({
  opened,
  athletePackage,
  closeHandler,
  isDesktop,
  cancelAthletePackageMutation,
}) => (
  <CancelModalProvider
    athletePackage={athletePackage}
    isDesktop={isDesktop}
    closeHandler={closeHandler}
    cancelMutation={cancelAthletePackageMutation}
    opened={opened}
  >
    <CancelModalShell opened={opened} />
  </CancelModalProvider>
);

export default CancelModal;
